import { useCallback, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { exerciseRunsApi, type Exercise, type ExerciseRun } from '@/api/exercises'
import { ROUTES } from '@/utils/constants'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'

type Options = {
  lessonId: number
  reload: (background?: boolean) => Promise<void>
  setError: (value: string | null) => void
}

/**
 * Действия над заданиями урока: выдать, снять, решать.
 *
 * Общие для списка внутри темы и для блока работ вне программы — иначе два
 * набора обработчиков разъезжались бы по поведению кнопок и блокировок.
 */
export function useLessonExerciseActions({ lessonId, reload, setError }: Options) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [busyId, setBusyId] = useState<number | null>(null)

  // Двойник busyId в ссылке: обработчик кнопки читает состояние из замыкания
  // своего рендера, и два быстрых клика успевали пройти оба до перерисовки —
  // класс получал две выдачи одного задания.
  const busyRef = useRef(false)

  const run = useCallback(
    async (exerciseId: number, action: () => Promise<void>) => {
      if (busyRef.current) return

      busyRef.current = true
      setBusyId(exerciseId)
      setError(null)
      try {
        await action()
      } catch (err) {
        // Отказы API — его текстом (выдача ученику без кабинета: «Доступно
        // после создания кабинета ученику»), технические — общей фразой
        setError(displayErrorMessage(getErrorMessage(err), t('common.error')))
      } finally {
        busyRef.current = false
        setBusyId(null)
      }
    },
    [setError, t],
  )

  const launch = useCallback(
    (exercise: Exercise) =>
      run(exercise.id, async () => {
        await exerciseRunsApi.launch(lessonId, exercise.id)
        await reload(true)
      }),
    [lessonId, reload, run],
  )

  const cancelRuns = useCallback(
    (exercise: Exercise, targets: ExerciseRun[]) =>
      run(exercise.id, async () => {
        // Список перезапрашиваем один раз в конце: по запросу на каждую отмену
        // давало пятнадцать лишних выборок на классе из пятнадцати человек.
        await Promise.all(targets.map((target) => exerciseRunsApi.cancel(target.id)))
        await reload(true)
      }),
    [reload, run],
  )

  const solve = useCallback(
    async (exercise: Exercise, mine: ExerciseRun | null) => {
      if (mine) {
        navigate(ROUTES.EXERCISE_RUN(mine.id))

        return
      }

      await run(exercise.id, async () => {
        // Урок называем явно: тренировка со страницы урока должна попасть в его
        // сводку, иначе учитель не увидит, что ученик перебирал ответы.
        const started = await exerciseRunsApi.selfRun(exercise.id, lessonId)
        navigate(ROUTES.EXERCISE_RUN(started.id))
      })
    },
    [lessonId, navigate, run],
  )

  return { busyId, launch, cancelRuns, solve }
}
